import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserSubscription, AssignSubscriptionData, SubscriptionPlan, mapDbPlanToSubscriptionPlan, SubscriptionStatus } from './types';
import { queryClient } from './queryClient';
import { CustomFeaturesData } from '@/lib/types';

// Fetch user subscription with plan details
export const fetchUserSubscription = async (): Promise<UserSubscription | null> => {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  
  if (userError || !userData.user) {
    console.error('Error getting user:', userError);
    throw userError;
  }

  const userId = userData.user.id;

  const { data: subscriptionData, error: subscriptionError } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (subscriptionError) {
    console.error('Error fetching user subscription:', subscriptionError);
    throw subscriptionError;
  }

  if (!subscriptionData) {
    return null; // No active subscription
  }

  const { data: planData, error: planError } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('id', subscriptionData.plan_id)
    .single();

  if (planError) {
    console.error('Error fetching subscription plan:', planError);
    throw planError;
  }

  return {
    id: subscriptionData.id,
    user_id: subscriptionData.user_id,
    plan_id: subscriptionData.plan_id,
    plan: mapDbPlanToSubscriptionPlan(planData),
    status: subscriptionData.status as SubscriptionStatus,
    current_period_start: subscriptionData.current_period_start,
    current_period_end: subscriptionData.current_period_end,
    stripe_customer_id: subscriptionData.stripe_customer_id,
    stripe_subscription_id: subscriptionData.stripe_subscription_id,
    created_at: subscriptionData.created_at,
    updated_at: subscriptionData.updated_at,
    is_admin_assigned: subscriptionData.is_admin_assigned,
    is_free_assignment: subscriptionData.is_free_assignment,
    admin_assigned_by: subscriptionData.admin_assigned_by,
    admin_assigned_at: subscriptionData.admin_assigned_at,
    expiry_warning_sent_at: subscriptionData.expiry_warning_sent_at,
    downgraded_at: subscriptionData.downgraded_at,
    downgraded_from_plan_id: subscriptionData.downgraded_from_plan_id,
    is_custom_plan: subscriptionData.is_custom_plan,
    custom_features: subscriptionData.custom_features as CustomFeaturesData | null
  };
};

// Fetch all user subscriptions (admin only)
export const fetchAllUserSubscriptions = async (): Promise<UserSubscription[]> => {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  
  if (userError || !userData.user) {
    console.error('Error getting user:', userError);
    throw userError;
  }

  const { data: subscriptionsData, error: subscriptionsError } = await supabase
    .from('user_subscriptions')
    .select('*');

  if (subscriptionsError) {
    console.error('Error fetching user subscriptions:', subscriptionsError);
    throw subscriptionsError;
  }

  if (!subscriptionsData || subscriptionsData.length === 0) {
    return [];
  }

  const { data: plansData, error: plansError } = await supabase
    .from('subscription_plans')
    .select('*');

  if (plansError) {
    console.error('Error fetching subscription plans:', plansError);
    throw plansError;
  }

  const plansMap = plansData.reduce((map: Record<string, any>, plan) => {
    map[plan.id] = plan;
    return map;
  }, {});

  return subscriptionsData.map(subscription => ({
    id: subscription.id,
    user_id: subscription.user_id,
    plan_id: subscription.plan_id,
    plan: plansMap[subscription.plan_id] ? mapDbPlanToSubscriptionPlan(plansMap[subscription.plan_id]) : undefined,
    status: subscription.status as SubscriptionStatus,
    current_period_start: subscription.current_period_start,
    current_period_end: subscription.current_period_end,
    stripe_customer_id: subscription.stripe_customer_id,
    stripe_subscription_id: subscription.stripe_subscription_id,
    created_at: subscription.created_at,
    updated_at: subscription.updated_at,
    is_admin_assigned: subscription.is_admin_assigned,
    is_free_assignment: subscription.is_free_assignment,
    admin_assigned_by: subscription.admin_assigned_by,
    admin_assigned_at: subscription.admin_assigned_at,
    expiry_warning_sent_at: subscription.expiry_warning_sent_at,
    downgraded_at: subscription.downgraded_at,
    downgraded_from_plan_id: subscription.downgraded_from_plan_id
  }));
};

// Create a user subscription - use the RPC function
export const createUserSubscription = async (subscriptionData: {
  plan_id: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  current_period_end: string;
}): Promise<UserSubscription> => {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    console.error('Error getting user:', userError);
    throw userError;
  }

  const { data: rpcResult, error } = await supabase.rpc('create_user_subscription', {
    p_plan_id: subscriptionData.plan_id,
    p_current_period_end: subscriptionData.current_period_end,
    p_stripe_customer_id: subscriptionData.stripe_customer_id || null,
    p_stripe_subscription_id: subscriptionData.stripe_subscription_id || null
  });

  if (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }

  return fetchUserSubscription();
};

// Admin function to assign a subscription to any user
export const assignUserSubscription = async (subscriptionData: AssignSubscriptionData): Promise<UserSubscription> => {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  
  if (userError || !userData.user) {
    console.error('Error getting user:', userError);
    throw userError;
  }

  const { data: existingSubscription, error: checkError } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', subscriptionData.user_id)
    .eq('status', 'active');

  if (checkError) {
    console.error('Error checking existing subscriptions:', checkError);
    throw checkError;
  }

  if (existingSubscription && existingSubscription.length > 0) {
    const { error: cancelError } = await supabase
      .from('user_subscriptions')
      .update({
        status: 'canceled',
        updated_at: new Date().toISOString()
      })
      .eq('id', existingSubscription[0].id);

    if (cancelError) {
      console.error('Error canceling existing subscription:', cancelError);
      throw cancelError;
    }
  }

  const { data, error } = await supabase
    .from('user_subscriptions')
    .insert([{
      user_id: subscriptionData.user_id,
      plan_id: subscriptionData.plan_id,
      status: 'active',
      current_period_start: subscriptionData.current_period_start,
      current_period_end: subscriptionData.current_period_end,
      stripe_customer_id: subscriptionData.stripe_customer_id || null,
      stripe_subscription_id: subscriptionData.stripe_subscription_id || null,
      is_admin_assigned: subscriptionData.is_admin_assigned || false,
      is_free_assignment: subscriptionData.is_free_assignment || false,
      admin_assigned_by: subscriptionData.admin_assigned_by || null,
      admin_assigned_at: subscriptionData.is_admin_assigned ? new Date().toISOString() : null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }

  return data as UserSubscription;
};

// Cancel a user subscription
export const cancelUserSubscription = async (subscriptionId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_subscriptions')
    .update({
      status: 'canceled',
      updated_at: new Date().toISOString()
    })
    .eq('id', subscriptionId);

  if (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

// React Query hooks for user subscriptions
export const useUserSubscriptionHooks = () => {
  // Query hooks
  const useCurrentUserSubscriptionQuery = () => 
    useQuery({
      queryKey: ['user-subscription'],
      queryFn: fetchUserSubscription
    });

  const useAllUserSubscriptionsQuery = () => 
    useQuery({
      queryKey: ['all-user-subscriptions'],
      queryFn: fetchAllUserSubscriptions
    });

  // Mutation hooks
  const useCreateUserSubscriptionMutation = () => 
    useMutation({
      mutationFn: createUserSubscription,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-subscription'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-features'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-access'] });
        queryClient.invalidateQueries({ queryKey: ['custom-plan-info'] });
      }
    });

  const useAssignUserSubscriptionMutation = () => 
    useMutation({
      mutationFn: assignUserSubscription,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['all-user-subscriptions'] });
        queryClient.invalidateQueries({ queryKey: ['user-subscription'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-features'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-access'] });
        queryClient.invalidateQueries({ queryKey: ['custom-plan-info'] });
      }
    });

  const useCancelUserSubscriptionMutation = () => 
    useMutation({
      mutationFn: cancelUserSubscription,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-subscription'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-features'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-access'] });
        queryClient.invalidateQueries({ queryKey: ['custom-plan-info'] });
      }
    });

  return {
    useCurrentUserSubscriptionQuery,
    useAllUserSubscriptionsQuery,
    useCreateUserSubscriptionMutation,
    useAssignUserSubscriptionMutation,
    useCancelUserSubscriptionMutation
  };
};
